
import { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Unit, Owner, Vehicle, WaterReading, NewsItem,
    TariffCollection, UserPermission, InvoiceSettings, Adjustment, MonthlyStat, SystemMetadata, ChargeRaw,
    MiscRevenue, OperationalExpense
} from '../types';
import * as api from '../services/index'; 
import { get, set } from 'idb-keyval';
import { getPreviousPeriod } from '../utils/helpers';

const CACHE_PREFIX = 'qhome_cache_v5_';
const META_KEY = 'qhome_meta_version';

export const useSmartSystemData = (currentUser: UserPermission | null) => {
    const [data, setData] = useState<any>({
        units: [], owners: [], vehicles: [], waterReadings: [], charges: [],
        adjustments: [], users: [], news: [], feedback: [], monthlyStats: [],
        lockedWaterPeriods: [], invoiceSettings: null,
        tariffs: { service: [], parking: [], water: [] },
        miscRevenues: [], expenses: [], otherFeeConfigs: [], registrations: [], hasLoaded: false
    });
    
    const [loading, setLoading] = useState(true);
    const isFetching = useRef(false);
    const lastFetchTs = useRef(0);

    const refreshSystemData = useCallback(async (force = false) => {
        if (isFetching.current || !currentUser) return;
        
        isFetching.current = true;
        setLoading(true);
        lastFetchTs.current = Date.now();
        
        try {
            const isAdmin = currentUser.Role !== 'Resident';
            const currentPeriod = new Date().toISOString().slice(0, 7);

            // Fetch metadata to check versions
            const metadata = await api.getSystemMetadata();

            // 1. Fetch static data with version check
            const fetchWithCache = async (key: string, versionKey: keyof SystemMetadata, fetchFn: () => Promise<any>) => {
                const cacheKey = `${CACHE_PREFIX}${key}`;
                const cached = await get(cacheKey);
                const currentVersion = metadata[versionKey] || 0;

                if (!force && cached && cached.version === currentVersion) {
                    return cached.data;
                }
                const freshData = await fetchFn();
                await set(cacheKey, { data: freshData, version: currentVersion });
                return freshData;
            };

            // Public / Static data
            const [fetchedNews, invoiceSettings] = await Promise.all([
                api.fetchNews().catch(() => []),
                api.fetchInvoiceSettings().catch(() => null)
            ]);

            if (!isAdmin) {
                // RESIDENT FLOW: Fetch only what's allowed
                if (currentUser.residentId) {
                    const [specific, charges] = await Promise.all([
                        api.fetchResidentSpecificData(currentUser.residentId).catch(() => ({ unit: null, owner: null, vehicles: [] })),
                        api.fetchChargesForResident(currentUser.residentId).catch(() => [])
                    ]);

                    setData((prev: any) => ({
                        ...prev,
                        units: specific.unit ? [specific.unit] : [],
                        owners: specific.owner ? [specific.owner] : [],
                        vehicles: (specific.vehicles || []).filter((v:any) => v.isActive),
                        charges: charges,
                        news: fetchedNews || [],
                        invoiceSettings,
                        hasLoaded: true
                    }));
                } else {
                    setData((prev: any) => ({ ...prev, news: fetchedNews, invoiceSettings, hasLoaded: true }));
                }
            } else {
                // ADMIN FLOW: Stage 1 (Fast Overview)
                const periodsToFetch = [currentPeriod, getPreviousPeriod(currentPeriod)];
                
                const [stats, charges, otherFees] = await Promise.all([
                    api.fetchCollection('monthly_stats').catch(() => []),
                    api.fetchActiveCharges(periodsToFetch, force).catch(() => []),
                    api.fetchOtherFeeConfigs().catch(() => [])
                ]);

                setData((prev: any) => ({
                    ...prev,
                    news: fetchedNews || [],
                    invoiceSettings,
                    monthlyStats: stats || [],
                    charges: charges || [],
                    otherFeeConfigs: otherFees || [],
                    hasLoaded: true
                }));

                // ADMIN FLOW: Stage 2 (Heavy Static Data with Cache)
                const [units, owners, vehicles, tariffsData, fetchedUsers, exps, water, locks, misc] = await Promise.all([
                    fetchWithCache('units', 'units_version', () => api.fetchCollection('units')),
                    fetchWithCache('owners', 'owners_version', () => api.fetchCollection('owners')),
                    fetchWithCache('vehicles', 'vehicles_version', () => api.fetchCollection('vehicles')),
                    fetchWithCache('tariffs', 'tariffs_version', () => api.fetchTariffsData()),
                    fetchWithCache('users', 'users_version', () => api.fetchCollection('users')),
                    api.fetchCollection('operational_expenses'),
                    api.fetchRecentWaterReadings(periodsToFetch, force).catch(() => []),
                    api.fetchWaterLocks().catch(() => []),
                    api.getMonthlyMiscRevenues(currentPeriod).catch(() => [])
                ]);

                setData((prev: any) => ({
                    ...prev,
                    units: units || [], 
                    owners: owners || [], 
                    vehicles: vehicles || [],
                    tariffs: tariffsData || { service: [], parking: [], water: [] },
                    users: fetchedUsers || [],
                    expenses: exps || [],
                    waterReadings: water || [],
                    lockedWaterPeriods: locks || [],
                    miscRevenues: misc || [],
                }));
            }
        } catch (err) {
            console.error("Smart Sync Error:", err);
            setData((prev: any) => ({ ...prev, hasLoaded: true }));
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, [currentUser]);

    // Real-time subscriptions for dynamic data
    useEffect(() => {
        if (!currentUser || currentUser.Role === 'Resident') return;

        const unsubFeedback = api.subscribeToCollection('feedback', (data) => {
            setData((prev: any) => ({ ...prev, feedback: data }));
        });

        const unsubRegistrations = api.subscribeToCollection('registrations', (data) => {
            setData((prev: any) => ({ ...prev, registrations: data }));
        });

        return () => {
            unsubFeedback();
            unsubRegistrations();
        };
    }, [currentUser]);

    useEffect(() => {
        if (currentUser) refreshSystemData();
    }, [currentUser?.residentId, refreshSystemData]);

    return { ...data, loading, refreshSystemData };
};
